import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { themeGray, themeOrange } from "../../../utils/colorTheme";
import { useLocation, useNavigate } from "react-router-dom";
import { apiHandle } from "../../../config/apiHandle/apiHandle";
import { IoArrowBack } from "react-icons/io5";

const ChatScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, vendorId, userName, vendorName, userAvatar, vendorAvatar } =
    location.state || {};

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const createRoomId = (id1, id2) => {
    // Convert to numbers first, then sort numerically to match frontend behavior
    const ids = [Number(id1), Number(id2)].sort((a, b) => a - b);
    return `${ids[0]}-${ids[1]}`;
  };

  useEffect(() => {
    if (!userId || !vendorId) {
      navigate("/message");
      return;
    }

    let cancelled = false;

    const fetchMessages = async () => {
      try {
        const res = await apiHandle.get(`/admin/messages/${userId}/${vendorId}`);
        if (cancelled) return;
        const data = res.data?.messages ?? res.data?.data ?? [];
        setMessages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load admin messages:", e?.response?.data || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMessages();
    const pollId = setInterval(fetchMessages, 3000);

    return () => {
      cancelled = true;
      clearInterval(pollId);
    };
  }, [userId, vendorId, navigate]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const messageDate = message.createdAt || message.created_at || new Date().toISOString();
    const date = new Date(messageDate).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push({
      ...message,
      createdAt: messageDate, // normalize the field
    });
    return groups;
  }, {});

  return (
    <div style={{ position: "relative", backgroundColor: themeGray }}>
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Box
            onClick={() => navigate("/message")}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#eee",
              },
            }}
          >
            <IoArrowBack size={24} color="#333" />
          </Box>
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              color: "black",
              fontSize: "28px",
              fontWeight: "bold",
            }}
          >
            Conversation
          </Typography>
        </Stack>
        <Divider />
      </Stack>

      {/* Conversation Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={4}
        sx={{
          backgroundColor: "white",
          padding: "16px",
          marginTop: "10px",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Avatar src={userAvatar} sx={{ width: 50, height: 50 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
              {userName}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#888" }}>
              Customer
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            fontSize: "24px",
            color: themeOrange,
            fontWeight: "bold",
          }}
        >
          ↔
        </Typography>

        <Stack direction="row" alignItems="center" gap={2}>
          <Avatar src={vendorAvatar} sx={{ width: 50, height: 50 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
              {vendorName}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#888" }}>
              Pro
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Messages Container */}
      <Stack
        sx={{
          marginTop: "10px",
          backgroundColor: "white",
          borderRadius: "10px",
          height: "65vh",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%" }}
          >
            <CircularProgress size={30} sx={{ color: themeOrange }} />
            <Typography sx={{ mt: 2, color: "#888" }}>
              Loading messages...
            </Typography>
          </Stack>
        ) : messages.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%" }}
          >
            <Typography sx={{ color: "#888", fontSize: "16px" }}>
              No messages in this conversation yet
            </Typography>
          </Stack>
        ) : (
          <Stack
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: "20px",
            }}
            gap={2}
          >
            {Object.entries(groupedMessages).map(([date, msgs]) => (
              <Box key={date}>
                {/* Date Separator */}
                <Stack alignItems="center" my={2}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#888",
                      backgroundColor: "#f5f5f5",
                      padding: "4px 12px",
                      borderRadius: "12px",
                    }}
                  >
                    {formatDate(date)}
                  </Typography>
                </Stack>

                {/* Messages for this date */}
                {msgs.map((msg, index) => {
                  // Handle both string and number comparison for senderId
                  const msgSenderId = String(msg.senderId || msg.sender_id || "");
                  const isFromUser = msgSenderId === String(userId);
                  const senderName = isFromUser ? userName : vendorName;
                  const senderAvatar = isFromUser ? userAvatar : vendorAvatar;
                  const senderLabel = isFromUser ? "Customer" : "Pro";

                  return (
                    <Stack
                      key={index}
                      direction="row"
                      justifyContent={isFromUser ? "flex-start" : "flex-end"}
                      sx={{ mb: 1.5 }}
                    >
                      <Stack
                        direction={isFromUser ? "row" : "row-reverse"}
                        gap={1}
                        sx={{ maxWidth: "70%" }}
                      >
                        <Avatar
                          src={senderAvatar}
                          sx={{ width: 36, height: 36 }}
                        />
                        <Box>
                          <Stack
                            direction={isFromUser ? "row" : "row-reverse"}
                            alignItems="center"
                            gap={1}
                            mb={0.5}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#333",
                              }}
                            >
                              {senderName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px",
                                color: isFromUser ? "#2196f3" : themeOrange,
                                fontWeight: 500,
                              }}
                            >
                              ({senderLabel})
                            </Typography>
                          </Stack>
                          <Box
                            sx={{
                              backgroundColor: isFromUser ? "#e3f2fd" : "#fff3e0",
                              padding: "10px 14px",
                              borderRadius: isFromUser
                                ? "4px 16px 16px 16px"
                                : "16px 4px 16px 16px",
                              border: `1px solid ${
                                isFromUser ? "#bbdefb" : "#ffe0b2"
                              }`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                                lineHeight: 1.5,
                              }}
                            >
                              {msg.message || msg.content || ""}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#999",
                              mt: 0.5,
                              textAlign: isFromUser ? "left" : "right",
                            }}
                          >
                            {formatTime(msg.createdAt)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            ))}
          </Stack>
        )}

        {/* Read-only notice */}
        <Stack
          sx={{
            padding: "12px 20px",
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid #eee",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              color: "#888",
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            This is a read-only view of the conversation between the customer and pro
          </Typography>
        </Stack>
      </Stack>
    </div>
  );
};

export default ChatScreen;
